/**
 * MemoPrivacyToggle — the Shared / Private dropdown for a memo (note or
 * task). One control, two skins:
 *   - `icon` (default): a size-6 icon-only trigger with a tooltip, for the
 *     composer's title row where it sits beside the owner avatar.
 *   - `labeled`: icon + "Shared"/"Private" text, for a record overview row
 *     where the value must read without hovering.
 *
 * Privacy is memo-kind generic on the backend (resolve_memo_visibility_names),
 * so this is deliberately not note-specific. The owner/creator gate is the
 * caller's (`isDisabled`).
 */

import { Lock01, Users01 } from '@untitledui/icons';
import { Button as AriaButton } from 'react-aria-components';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Tooltip } from '@/lib/base/tooltip/tooltip';

import { MEMO_ICON_BUTTON_CLASS } from './memoIconButton';

export type MemoPrivacy = 'private' | 'shared';

// Same visual language as the icon trigger, widened to carry the label;
// matches the read-affordance pills the task overview's other rows use.
const LABELED_TRIGGER_CLASS =
  'inline-flex h-6 shrink-0 items-center gap-1.5 rounded-md px-1.5 text-sm text-secondary hover:bg-primary_hover hover:text-tertiary_hover disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer outline-brand focus-visible:outline-2 focus-visible:outline-offset-2';

export interface MemoPrivacyToggleProps {
  privacy: MemoPrivacy;
  /** `true` = shared with the org, `false` = private (owner + creator only). */
  onChange: (isShared: boolean) => void;
  isDisabled?: boolean;
  variant?: 'icon' | 'labeled';
}

export function MemoPrivacyToggle({
  privacy,
  onChange,
  isDisabled = false,
  variant = 'icon',
}: MemoPrivacyToggleProps) {
  const label = privacy === 'shared' ? 'Shared' : 'Private';
  const Icon = privacy === 'shared' ? Users01 : Lock01;

  const trigger =
    variant === 'labeled' ? (
      <AriaButton
        aria-label='Set visibility'
        isDisabled={isDisabled}
        className={LABELED_TRIGGER_CLASS}
      >
        <Icon className='size-4' aria-hidden='true' />
        <span>{label}</span>
      </AriaButton>
    ) : (
      <Tooltip title={label}>
        <AriaButton
          aria-label='Set visibility'
          isDisabled={isDisabled}
          className={MEMO_ICON_BUTTON_CLASS}
        >
          <Icon className='size-4' />
        </AriaButton>
      </Tooltip>
    );

  return (
    <Dropdown.Root>
      {trigger}
      <Dropdown.Popover placement='bottom end'>
        <Dropdown.Menu
          onAction={key => onChange(key === 'shared')}
          selectedKeys={new Set([privacy])}
          selectionMode='single'
        >
          <Dropdown.Item key='shared' id='shared' icon={Users01}>
            Shared
          </Dropdown.Item>
          <Dropdown.Item key='private' id='private' icon={Lock01}>
            Private
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
